import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import FormControlLabel from '@mui/material/FormControlLabel';
import { trackEventBudget } from 'helpers/trackerHelpers';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import HistoryIcon from '@mui/icons-material/History';

import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import ExportButtonMultipleGrid from 'components/ExportButton/ExportButtonMultipleGrid';
import { useDispatch } from 'store/hooks';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';

import GroupActionButton from 'routes/Admin/Budget/shared/GroupActionButton';

const StyRightActions = styled(Box, {
    name: 'StyRightActions',
})({
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
});

const StyRefreshButton = styled(Button, {
    name: 'StyRefreshButton',
})({
    height: '26px',
    minHeight: '26px',
    marginRight: '16px',
});

type TabActionsTrackedEvents = {
    export: string;
    refreshData: string;
    historyModal?: string;
};

type Props = {
    hideEmpty?: boolean;
    setHideEmpty?: (value: boolean) => void;
    queryToRefresh?: string | (string | number)[] | InscriptionQueryKey;
    trackedEvents: TabActionsTrackedEvents;
    toggleLabel: string;
    fileName?: string;
    showHistoryButton?: boolean;
    setHistoryModalOpen?: Dispatch<SetStateAction<boolean>>;
};

export default function TabActions({
    hideEmpty = false,
    setHideEmpty,
    queryToRefresh,
    toggleLabel,
    trackedEvents,
    fileName,
    showHistoryButton = false,
    setHistoryModalOpen = undefined,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const refreshLastColumn = () => {
        trackEventBudget(trackedEvents.refreshData);
        queryClient
            .invalidateQueries({
                queryKey: [queryToRefresh],
            })
            .finally(() => {
                dispatch.vision.notifyUser({
                    message: t('budgetOverview.dataRefreshed'),
                    snackType: 'success',
                });
            });
    };

    const openHistoryModal = () => {
        if (setHistoryModalOpen) {
            setHistoryModalOpen(true);
            if (trackedEvents.historyModal) {
                trackEventBudget(trackedEvents.historyModal);
            }
        }
    };

    return (
        <StyRightActions>
            {showHistoryButton && (
                <GroupActionButton
                    title={t('allInscriptions.openHistory')}
                    onClick={openHistoryModal}
                    icon={<HistoryIcon />}
                />
            )}
            {fileName && (
                <ExportButtonMultipleGrid
                    event={trackedEvents.export}
                    fileName={fileName}
                />
            )}
            {queryToRefresh && queryToRefresh.length > 0 && (
                <StyRefreshButton
                    variant="contained"
                    onClick={refreshLastColumn}
                >
                    {t('budgetOverview.refreshData')}
                </StyRefreshButton>
            )}
            {setHideEmpty && (
                <FormControlLabel
                    control={
                        <Switch
                            checked={hideEmpty}
                            onChange={(event) => {
                                setHideEmpty(event.target.checked);
                            }}
                            size="small"
                        />
                    }
                    label={
                        <Typography variant="body1" sx={{ fontSize: '0.8rem' }}>
                            {toggleLabel}
                        </Typography>
                    }
                />
            )}
        </StyRightActions>
    );
}
